import React, { useState, useEffect, useCallback } from 'react';
import commons from '@medflex/commons';
import InvitePatientForm from '@medflex/components/stories/InvitePatientForm';
import Modal from '@medflex/components/stories/Modal';
import { showSuccessToast } from '@medflex/components/stories/Toast';
import Logger from '@medflex/utils/Logger';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import { useTranslation } from 'react-i18next';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { dayjs } from '@medflex/shared-utils';
import { SubscriptionStatus } from '@medflex/shared-constants';
import useInvitePatient from '../hooks/useInvitePatient';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useIsModal from '../hooks/useIsModal';
import useModal from '../hooks/useModal';
import useSubscription from '../hooks/useSubscription';

const handleSubmit =
  ({ invite, setSubmitted }) =>
  values => {
    const { mobileNumber, email, firstName, lastName } = values;
    invite({
      ...(!mobileNumber && firstName && { firstName }),
      ...(!mobileNumber && lastName && { lastName }),
      ...(email && { email }),
      ...(mobileNumber && {
        mobileNumber: commons.phoneNumber.transformToE164(mobileNumber).phoneNumberE164,
      }),
    });
    setSubmitted(true);
  };

const InvitePatientModule = () => {
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation('physician');

  const { invitePatient: mode, inviteSent } = useLocationSearchParams();
  const { invite, isLoading, error } = useInvitePatient();

  const [status, setStatus] = useState();
  const { close, open } = useModal();
  // we need to pass these variables from the url into the form, otherwise the
  // invite link of our physicians contact form will be useless
  const { vorname = '', nachname = '', email = '' } = useLocationSearchParams();
  const closeModal = useCallback(
    ({ inviteSent = false } = {}) =>
      close(
        {
          invitePatient: mode,
          vorname,
          nachname,
          email,
          modal: 'contactInvite',
          inviteSent: true,
        },
        { inviteSent },
      ),
    [close, email, mode, nachname, vorname],
  );
  const { isModal } = useIsModal();
  const isWideScreen = useIsWideScreen();
  const { contractStatus, trialEndedAt, upgradeUrl } = useSubscription();

  const canSendSms =
    contractStatus === SubscriptionStatus.ACTIVE &&
    (!trialEndedAt || dayjs().isAfter(dayjs(trialEndedAt)));
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (submitted && !isLoading) {
      setSubmitted(false);
      if (error.invite) {
        const { response } = error.invite;
        Logger.error('invite patient', error.invite, response);
        if (response && response.status === 409) {
          trackEvent({
            zone: 'InvitePatientModalModule',
            action: TRACKING_ACTIONS.SUBMIT,
            elementType: TRACKING_ELEMENT_TYPES.FORM,
            actionText: `Invitation via ${mode} Failed - Duplicate`,
          });
          return setStatus({ apiError: 'already_invited' });
        }
        setStatus({ apiError: response ? 'api_error' : 'network_error' });
        return trackEvent({
          zone: 'InvitePatientModalModule',
          action: TRACKING_ACTIONS.SUBMIT,
          elementType: TRACKING_ELEMENT_TYPES.FORM,
          actionText: `Invitation via ${mode} Failed - ${response.status}`,
        });
      }
      trackEvent({
        zone: 'InvitePatientModalModule',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.FORM,
        actionText: `Invite Contact via ${mode} successful`,
      });
      closeModal({ ...(inviteSent === 'true' && { inviteSent: true }) });
      showSuccessToast(
        t('toasts.invite.title_success'),
        <span>
          <button
            type="button"
            className="text-blue-500"
            onClick={() => open({ invitePatient: mode })}
          >
            {t('toasts.invite.text_success')}
          </button>
        </span>,
        { toastId: 'InvitePatientSuccess' },
      );
    }
  }, [submitted, isLoading, error, mode, closeModal, open]);

  const handleClose = () => {
    trackEvent({
      zone: 'InvitePatientModalModule',
      actionText: TRACKING_ACTIONS.CLOSE,
      elementType: TRACKING_ELEMENT_TYPES.FORM,
    });
    closeModal();
  };

  const handleClickMultiPatientUpload = () =>
    open({ modal: 'multiPatientInvitation', invitePatient: '' });

  const handleClickPermanentLink = () => open({ modal: 'patientPermanentLink', invitePatient: '' });
  const handleQRCodeLinkClick = () => open({ modal: 'patientPermanentQRCode', invitePatient: '' });

  return (
    <Modal
      infoUrl="https://medflex.de/hilfe/behandler-kontakte/patienten-einladen/"
      title={t('components:InvitePatientForm.title')}
      showCloseButton
      onClickCloseButton={handleClose}
    >
      <InvitePatientForm
        isMobileNumberMode={mode === 'sms'}
        firstName={vorname}
        lastName={nachname}
        email={email}
        onModeSwitch={({ value }) => open({ invitePatient: value })}
        onSubmit={handleSubmit({ invite, setSubmitted })}
        onClickQRCodeLink={handleQRCodeLinkClick}
        onClickMultiPatientUpload={handleClickMultiPatientUpload}
        onClickPermanentLink={handleClickPermanentLink}
        autofocus={isModal && isWideScreen}
        status={status}
        canSendSms={canSendSms}
        smsUpgradeUrl={upgradeUrl}
        isLoading={isLoading}
      />
    </Modal>
  );
};

const InvitePatientsModalModule = () => {
  const { invitePatient } = useLocationSearchParams();

  if (invitePatient && ['email', 'sms'].indexOf(invitePatient) !== -1)
    return <InvitePatientModule />;

  return null;
};

export default InvitePatientsModalModule;
